import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Dashboard settings interface
 * Maps dashboard widget keys to their visibility state
 */
export interface DashboardSettings {
  [key: string]: boolean;
}

/**
 * User preferences state interface
 * Manages user-specific preferences and settings
 */
export interface UserPreferencesState {
  /**
   * Instance timezone preference
   * Format: IANA timezone string (e.g., 'America/New_York')
   */
  instanceTimezone: string | null;

  /**
   * The viewer's own DISPLAY timezone, from public.user_preferences.timezone.
   * IANA name, or null when they have never had one set.
   *
   * Read through useDisplayTz() rather than directly: the hook applies the
   * resolution chain (stored zone, then the UTC floor) and validates the stored
   * value, which a raw selector would skip.
   */
  userTimezone: string | null;

  /**
   * Dashboard widget visibility settings
   * Maps widget identifiers to their visibility state
   */
  dashboardSettings: DashboardSettings;

  /**
   * Dashboard widget order
   * Array of widget keys determining display order
   */
  dashboardWidgetOrder: string[];

  /**
   * Last visited route for session restoration
   */
  lastVisitedRoute: string | null;
}

const initialState: UserPreferencesState = {
  instanceTimezone: null,
  userTimezone: null,
  dashboardSettings: {},
  dashboardWidgetOrder: [],
  lastVisitedRoute: null,
};

/**
 * User Preferences slice for managing user-specific settings
 *
 * This slice handles user preferences that should persist across sessions,
 * including timezone settings, dashboard widget visibility, and navigation state.
 *
 * @example
 * ```typescript
 * // Set timezone
 * dispatch(setInstanceTimezone('America/New_York'));
 *
 * // Update dashboard settings
 * dispatch(setDashboardSettings({ widget1: true, widget2: false }));
 *
 * // Toggle individual widget
 * dispatch(toggleDashboardWidget('widget1'));
 *
 * // Set last visited route
 * dispatch(setLastVisitedRoute('/dashboard'));
 *
 * // Reset all preferences
 * dispatch(resetUserPreferences());
 * ```
 */
const userPreferencesSlice = createSlice({
  name: "userPreferences",
  initialState,
  reducers: {
    /**
     * Set the instance timezone preference
     * @param state - Current user preferences state
     * @param action - Payload containing the IANA timezone string
     */
    setInstanceTimezone: (state, action: PayloadAction<string>) => {
      state.instanceTimezone = action.payload;
    },

    /**
     * Set the viewer's display timezone. Dispatched by the loader on boot and by
     * the picker on save, so a change is reflected app-wide without a reload.
     */
    setUserTimezone: (state, action: PayloadAction<string | null>) => {
      state.userTimezone = action.payload;
    },

    /**
     * Set all dashboard widget visibility settings
     * @param state - Current user preferences state
     * @param action - Payload containing dashboard settings object
     */
    setDashboardSettings: (state, action: PayloadAction<DashboardSettings>) => {
      state.dashboardSettings = action.payload;
    },

    /**
     * Toggle visibility of a specific dashboard widget
     * @param state - Current user preferences state
     * @param action - Payload containing the widget key to toggle
     */
    toggleDashboardWidget: (state, action: PayloadAction<string>) => {
      const widgetKey = action.payload;
      state.dashboardSettings[widgetKey] = !state.dashboardSettings[widgetKey];
    },

    /**
     * Set dashboard widget order
     * @param state - Current user preferences state
     * @param action - Payload containing array of widget keys in desired order
     */
    setDashboardWidgetOrder: (state, action: PayloadAction<string[]>) => {
      state.dashboardWidgetOrder = action.payload;
    },

    /**
     * Set the last visited route for session restoration
     * @param state - Current user preferences state
     * @param action - Payload containing the route path
     */
    setLastVisitedRoute: (state, action: PayloadAction<string>) => {
      state.lastVisitedRoute = action.payload;
    },

    /**
     * Reset all user preferences to initial state
     * @param state - Current user preferences state
     */
    resetUserPreferences: (state) => {
      state.instanceTimezone = null;
      state.userTimezone = null;
      state.dashboardSettings = {};
      state.dashboardWidgetOrder = [];
      state.lastVisitedRoute = null;
    },
  },
});

export const {
  setInstanceTimezone,
  setUserTimezone,
  setDashboardSettings,
  setDashboardWidgetOrder,
  toggleDashboardWidget,
  setLastVisitedRoute,
  resetUserPreferences,
} = userPreferencesSlice.actions;

export default userPreferencesSlice.reducer;
